/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useMemo, useRef } from "react"

import { InlineAlert } from "~/components/design-system/components"
import { FlagLink } from "~/features/feature-flags/client/FlagControls"
import { useFlagPanel } from "~/features/feature-flags/client/FlagPanelProvider"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { api, type RouterOutputs } from "~/utils/api"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { HISTORY_LIMIT } from "~/features/feature-flags/shared/contracts"
import { NO_MATCHES, type CategoryFilterValue } from "~/features/feature-flags/client/model/filters"
import {
  filterHistory,
  historyChangeLabel,
  historyTargetName,
} from "~/features/feature-flags/client/model/history"
import {
  flagName,
  isFeatureFlagKey,
  type FeatureFlagKey,
} from "~/features/feature-flags/shared/registry"

type HistoryRow = RouterOutputs["featureFlagsPanel"]["history"][number]

function FlagNameCell({ data }: ICellRendererParams<HistoryRow>) {
  if (!data) return null
  if (!isFeatureFlagKey(data.flagKey)) return <span>{data.flagKey}</span>
  return <FlagLink flagKey={data.flagKey} />
}

/** Who changed which flag, when, and why. Filter by flag on the server, and by category and search
 * here. */
export function FlagHistoryTable({
  flagKey,
  category,
  search,
}: {
  flagKey?: FeatureFlagKey
  category: CategoryFilterValue
  search: string
}) {
  const panel = useFlagPanel()
  const gridRef = useRef<AgGridReact<HistoryRow>>(null)
  const { data, isLoading } = api.featureFlagsPanel.history.useQuery({ flagKey })
  const rows = useMemo(
    () => filterHistory(data ?? [], { category, search }, panel),
    [data, category, search, panel],
  )

  const columnDefs = useMemo<ColDef<HistoryRow>[]>(
    () => [
      {
        colId: "changedAt",
        headerName: "When",
        valueGetter: ({ data }) => data?.changedAt,
        valueFormatter: ({ value }) => formatDate(value, DateFormatType.DayMonthYearWithTime),
        sort: "desc",
        width: 170,
        minWidth: 170,
      },
      ...(flagKey
        ? []
        : [
            {
              colId: "flag",
              headerName: "Flag",
              valueGetter: ({ data }) => (data ? flagName(data.flagKey) : ""),
              cellRenderer: FlagNameCell,
              width: 200,
            } satisfies ColDef<HistoryRow>,
          ]),
      {
        colId: "target",
        headerName: "For",
        valueGetter: ({ data }) => (data ? historyTargetName(data, panel) : ""),
        flex: 1,
        minWidth: 140,
      },
      {
        colId: "change",
        headerName: "Change",
        valueGetter: ({ data }) => (data ? historyChangeLabel(data) : ""),
        width: 130,
      },
      {
        colId: "note",
        headerName: "Note",
        field: "note",
        flex: 1,
        minWidth: 100,
        tooltip: ({ data }) => data?.note,
      },
      {
        colId: "changedBy",
        headerName: "Changed by",
        valueGetter: ({ data }) => (data ? panel.userName(data.changedBy) : ""),
        flex: 1,
        minWidth: 170,
      },
    ],
    [flagKey, panel],
  )

  return (
    <>
      <DataTableBaseV2<HistoryRow>
        id={flagKey ? `feature-flag-history-${flagKey}` : "feature-flags-history"}
        gridRef={gridRef}
        rowData={rows}
        columnDefs={columnDefs}
        loading={isLoading || panel.loadingUsers}
        showPagination={false}
        compact
        disableColumnSizingPersistence
        emptyMessage={NO_MATCHES}
      />
      {data && data.length >= HISTORY_LIMIT && (
        <InlineAlert
          className="shrink-0 mt-2"
          tone="info"
          size="compact"
          description={`Showing the latest ${HISTORY_LIMIT} changes.`}
        />
      )}
    </>
  )
}
